import { styled } from '@mui/material/styles';
// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { Typography } from '../../../lib/base';

const TextTypography = styled(Typography)(() => ({
  minWidth: '0',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const WrapRow = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'rowBorder' && prop !== 'firstCol' && prop !== 'isLastRow' && prop !== 'isFirstRow' && prop !== 'sx',
})<{
  firstCol: boolean;
  isLastRow: boolean;
  isFirstRow: boolean;
  rowBorder: boolean;
}>(({ theme, firstCol, isLastRow, isFirstRow, rowBorder }) => ({
  minHeight: theme.spacing(3),
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'nowrap',
  borderTop: rowBorder ? `1px solid ${theme.palette.divider}` : undefined,
  ...(firstCol && {
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      paddingRight: theme.spacing(2),
    },
  }),
  ...(isLastRow && {
    paddingTop: theme.spacing(1 / 2),
  }),
  ...(isFirstRow && {
    paddingBottom: theme.spacing(1 / 2),
  }),
  ...(!isLastRow &&
    !isFirstRow && {
      paddingTop: theme.spacing(1 / 2),
      paddingBottom: theme.spacing(1 / 2),
    }),
}));

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
  },
}));

const FirstCol = styled('div')(() => ({
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
}));

const SecondCol = styled('div')(() => ({
  flex: '1 1 auto',
  minWidth: '0',
  display: 'flex',
  flexDirection: 'column',
}));

const Label = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

interface Props {
  columns: Array<
    [string, React.ReactElement | string | number] | [string, React.ReactElement | string | number, number]
  >;
}
function TableView({ columns }: Props): React.ReactElement {
  const wrapRow = (
    element: string | React.ReactElement | number,
    idx: number,
    height?: number | null,
    firstCol?: boolean,
  ) => (
    <WrapRow
      key={idx}
      firstCol={!!firstCol}
      isLastRow={idx === columns.length - 1}
      isFirstRow={idx === 0}
      rowBorder={idx !== 0}
      style={height == null ? undefined : { height }}
    >
      {typeof element === 'string' || typeof element === 'number' ? (
        <TextTypography key="hash" variant="body1">
          {element}
        </TextTypography>
      ) : (
        element
      )}
    </WrapRow>
  );
  return (
    <Root>
      <FirstCol>
        {columns.map((column, idx) =>
          wrapRow(
            <Label key={column[0]} variant="body1">
              {column[0]}
            </Label>,
            idx,
            column.length === 3 ? column[2] : null,
            true,
          ),
        )}
      </FirstCol>
      <SecondCol>
        {columns.map((column, idx) => wrapRow(column[1], idx, column.length === 3 ? column[2] : null))}
      </SecondCol>
    </Root>
  );
}

export default TableView;
